import { isApiError } from '../api/errors';
import { get, patch, post, put } from '../api/coreApi';
import {
  Ticket,
  TicketStatus,
  TicketConversation,
} from '../components/CommandCentre/utils/commandCenterTypes';

export interface TicketListFilters {
  instanceId?: number;
  status?: string;
  urgency?: string;
  assignedToUserId?: number;
  createdByUserId?: number;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateTicketPayload {
  sender: { id: number; type: 'instance' | 'user' } | null;
  task_name: string;
  task_type: string;
  task_urgency: string;
  task_description: string;
  assignee_user_ids?: number[];
  instanceId: number;
}

export interface UpdateTicketPayload {
  assignees?: number[];
  login_unique_id?: string;
  notifyUserIds?: number[];
  instanceId?: number;
}

export interface UpdateTicketStatusPayload {
  status: TicketStatus;
}

interface TicketListResponse {
  tickets?: Ticket[];
}

const normalizeTicketStatus = (value: unknown): TicketStatus => {
  const normalized = String(value || '').trim().toLowerCase();

  switch (normalized) {
    case 'completed':
    case 'closed':
      return 'completed';
    case 'in-progress':
    case 'in progress':
      return 'in-progress';
    case 'on-hold':
    case 'on hold':
    case 'waiting':
      return 'on-hold';
    case 'open':
    default:
      return 'open';
  }
};

const normalizeTicket = (ticket: any): Ticket => ({
  ...ticket,
  instanceId: ticket.instanceId ?? ticket.instance_id,
  createdByUserId: ticket.createdByUserId ?? ticket.created_by_user_id,
  task_name: ticket.task_name ?? ticket.taskName ?? '',
  task_type: ticket.task_type ?? ticket.taskType ?? '',
  task_urgency: ticket.task_urgency ?? ticket.taskUrgency ?? 'minor',
  task_status: normalizeTicketStatus(ticket.task_status ?? ticket.taskStatus ?? 'open'),
  task_description: ticket.task_description ?? ticket.taskDescription ?? '',
  createdAt: ticket.createdAt ?? ticket.created_at ?? '',
  updatedAt: ticket.updatedAt ?? ticket.updated_at ?? ticket.createdAt ?? ticket.created_at ?? '',
  conversation: Array.isArray(ticket.conversation) ? ticket.conversation : [],
  assignees: Array.isArray(ticket.assignees) ? ticket.assignees : [],
});

const normalizeTickets = (tickets: unknown): Ticket[] =>
  Array.isArray(tickets) ? tickets.map(normalizeTicket) : [];

export interface TicketDetailState {
  status: 'idle' | 'loading' | 'loaded' | 'unauthorized' | 'not-found' | 'unavailable' | 'error';
  ticket: Ticket | null;
  error: string | null;
}

const notConnectedMessage =
  'The Command Centre ticket service has not been connected to Elite Core yet.';

const remapUnavailableError = (error: unknown): never => {
  if (isApiError(error) && error.kind === 'unavailable') {
    throw Object.assign(new Error(notConnectedMessage), error, {
      message: notConnectedMessage,
    });
  }

  throw error;
};

export const listTickets = async (): Promise<Ticket[]> => {
  try {
    const response = await get<TicketListResponse>('/command-center/tickets');
    return normalizeTickets(response.tickets);
  } catch (error) {
    return remapUnavailableError(error);
  }
};

export const listTicketsForFilters = async (filters?: TicketListFilters): Promise<Ticket[]> => {
  try {
    const response = await get<TicketListResponse>('/command-center/tickets', {
      params: filters,
    });
    return normalizeTickets(response.tickets);
  } catch (error) {
    return remapUnavailableError(error);
  }
};

export const getTicket = async (ticketId: number): Promise<Ticket> => {
  try {
    return normalizeTicket(await get<Ticket>(`/command-center/tickets/${ticketId}`));
  } catch (error) {
    return remapUnavailableError(error);
  }
};

export const createTicket = async (payload: CreateTicketPayload): Promise<Ticket> => {
  try {
    return normalizeTicket(await post<Ticket>('/command-center/create-ticket', { values: payload }));
  } catch (error) {
    return remapUnavailableError(error);
  }
};

export const updateTicket = async (ticketId: number, payload: UpdateTicketPayload): Promise<unknown> => {
  try {
    return await put(`/command-center/update-web-task-assignees/${ticketId}`, payload);
  } catch (error) {
    return remapUnavailableError(error);
  }
};

export const updateTicketStatus = async (
  ticketId: number,
  payload: UpdateTicketStatusPayload
): Promise<Ticket> => {
  try {
    return normalizeTicket(await patch<Ticket>(`/command-center/tickets/${ticketId}/status`, payload, undefined));
  } catch (error) {
    return remapUnavailableError(error);
  }
};

export const submitTicketConversation = async (formData: FormData): Promise<TicketConversation> => {
  try {
    return await post('/command-center/handle-conversation-submission', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } catch (error) {
    return remapUnavailableError(error);
  }
};

export { notConnectedMessage as commandCenterTicketUnavailableMessage };
